'use client';

import { useMemo } from 'react';
import NextLink from 'next/link';
import { Box, Button, Stack, Typography } from '@/primitives';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useMatingResultsUrls } from './matingResultsUrls';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useProjectedProgenyEbvChartData } from '@/hooks/publicSearch/mating/useProjectedProgenyEbvChartData';
import { useMatingPredictorStore } from '@/publicSearch/store/useMatingPredictorStore';
import PedigreeTab, {
  PEDIGREE_TYPE,
} from '@/components/animals/pedigree/PedigreeTab';
import EbvPercentileGraph from '@/components/publicSearch/animal/ebv/EbvPercentileGraph';
import type { PedigreeDisplayState } from './types';

interface MatingPedigreeDisplayProps {
  pedigreeState: PedigreeDisplayState;
}

export default function MatingPedigreeDisplay({
  pedigreeState,
}: MatingPedigreeDisplayProps) {
  const society = usePublicSearchContext((context) => context.society);
  const orgId = society.orgId;
  const { showPercentileGraph, showBreedplanHelp, useColourSlotGrouping } =
    useEbvDisplayOptions();
  const matingData = useMatingPredictorStore((state) => state.data);
  const { searchViewHref } = useMatingResultsUrls();

  const pairResult = useMemo(
    () =>
      matingData?.results.find(
        (result) =>
          result.sireId === pedigreeState.sireId &&
          result.damId === pedigreeState.damId,
      ),
    [matingData?.results, pedigreeState.damId, pedigreeState.sireId],
  );

  const {
    chartData: projectedEbvChartData,
    breedAverageMessage,
    hasPercentileData,
  } = useProjectedProgenyEbvChartData(society, pairResult?.projectedEbvs);

  const sireChartLabel =
    pedigreeState.sireName?.trim() || pedigreeState.sireIdent;
  const damChartLabel = pedigreeState.damName?.trim() || pedigreeState.damIdent;

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', pb: { xs: 3, md: 4 } }}>
      <Box sx={{ pt: { xs: 1, md: 1.5 }, mb: 2 }}>
        <Button
          component={NextLink}
          href={searchViewHref}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{ background: 'background.paper', whiteSpace: 'nowrap' }}
        >
          Back
        </Button>
      </Box>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={3}
        sx={{
          alignItems: 'stretch',
          mb: 3,
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
          }}
        >
          <Typography variant="titlemedium" sx={{ fontWeight: 700, mb: 1 }}>
            Sire Pedigree: {pedigreeState.sireIdent}
          </Typography>
          <PedigreeTab
            type={PEDIGREE_TYPE.PUBLIC}
            animalId={String(pedigreeState.sireId)}
            orgId={orgId}
            linkRootAnimal
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
          }}
        >
          <Typography variant="titlemedium" sx={{ fontWeight: 700, mb: 1 }}>
            Dam Pedigree: {pedigreeState.damIdent}
          </Typography>
          <PedigreeTab
            type={PEDIGREE_TYPE.PUBLIC}
            animalId={String(pedigreeState.damId)}
            orgId={orgId}
            linkRootAnimal
          />
        </Box>
      </Stack>

      {showPercentileGraph &&
        hasPercentileData &&
        projectedEbvChartData.length > 0 && (
          <Box
            sx={{
              width: { xs: '100%', md: 'fit-content' },
              maxWidth: '100%',
              mx: 'auto',
            }}
          >
            <EbvPercentileGraph
              data={projectedEbvChartData}
              centerTitle
              showBreedplanHelp={showBreedplanHelp}
              breedAverageMessage={breedAverageMessage}
              title={`Expected Progeny Average for Sire: ${sireChartLabel} × Dam: ${damChartLabel}`}
              // TODO: The Expected Progeny Average info modal is temporarily hidden
              // because its copy (ProjectedProgenyEbvInfoModal) has not been signed
              // off yet. Re-enable by removing `showInfoButton={false}` once the
              // correct content is confirmed and restoring the custom modal wiring.
              showInfoButton={false}
              useColourSlotGrouping={useColourSlotGrouping}
            />
          </Box>
        )}
    </Box>
  );
}
